import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send,
  Loader2,
  CheckCheck,
  Smile,
  Paperclip,
  Clock
} from 'lucide-react';
import Button from '@components/common/Button';
import mentorService from '@services/mentor.service';
import toast from 'react-hot-toast';

const MentorQuestionDetail = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  useEffect(() => {
    fetchQuestionDetail();
  }, [questionId]);

  const fetchQuestionDetail = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getQuestionById(questionId);
      // console.log('Question API Response:', response);
      
      if (response.success && response.data) {
        const questionData = response?.data?.data.question;
        // console.log('Question Data:', questionData);
        
        if (!questionData) {
          toast.error('Question data not found');
          return;
        }
        
        setQuestion(questionData);
        
        if (questionData.status !== 'pending') {
          setAnswerSubmitted(true);
        }
      } else {
        toast.error('Failed to load question');
      }
    } catch (error) {
      console.error('Failed to fetch question:', error);
      toast.error(error.response?.data?.message || 'Failed to load question details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    
    if (!answer.trim()) {
      toast.error('Please enter your answer');
      return;
    }

    try {
      setSubmitting(true);
      const response = await mentorService.answerQuestion(questionId, { answer: answer.trim() });
      
      if (response.success) {
        toast.success('Answer submitted successfully!');
        setAnswerSubmitted(true);
        const updatedQuestion = response.data?.question || {
          ...question,
          answer: answer.trim(),
          status: 'approved',
          answerDate: new Date().toISOString()
        };
        setQuestion(updatedQuestion);
        setAnswer('');
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Question not found</h3>
        <Button variant="outline" onClick={() => navigate('/mentor')}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 p-3 sm:p-4">
          <button
            onClick={() => navigate('/mentor')}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>

          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
            <span className="text-lg sm:text-xl font-bold text-white">
              {question.student?.firstName?.charAt(0) || 'S'}
            </span>
          </div>
          
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-primary-700 truncate">
              {question.student?.firstName && question.student?.lastName
                ? `${question.student.firstName} ${question.student.lastName}`
                : 'Student'}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              {question.student?.email || 'Student Question'}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
          </div>
        ) : (
          <>
            {/* Date Divider */}
            <div className="flex items-center justify-center my-4">
              <div className="bg-white px-3 py-1 rounded-full shadow-sm">
                <span className="text-xs text-gray-600 font-medium">
                  {formatDate(question.questionDate)}
                </span>
              </div>
            </div>

            {/* Student Question - Left Side (Mentor receives) */}
            <div className="flex justify-start">
              <div className="max-w-[75%] sm:max-w-[60%] bg-white text-gray-800 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl shadow-sm px-4 py-2.5">
                <p className="text-sm sm:text-base leading-relaxed break-words">
                  {question.question}
                </p>
                <div className="flex items-center gap-1 mt-1 justify-start">
                  <span className="text-xs text-gray-500">
                    {formatTime(question.questionDate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Mentor Answer - Right Side (Mentor sends) */}
            {question.status !== 'pending' && question.answer && (
              <>
                <div className="flex justify-end">
                  <div className="max-w-[75%] sm:max-w-[60%] bg-secondary-500 text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl px-4 py-2.5">
                    <p className="text-sm sm:text-base leading-relaxed break-words">
                      {question.answer}
                    </p>
                    <div className="flex items-center gap-1 mt-1 justify-end">
                      <span className="text-xs text-white/80">
                        {formatTime(question.answerDate)}
                      </span>
                      <CheckCheck className="w-3.5 h-3.5 text-white/80" />
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex justify-center mt-6">
                  {(question.status === 'answered' || question.status === 'approved') && (
                    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2">
                      <CheckCheck className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-semibold text-green-800">Answer Submitted Successfully</p>
                        <p className="text-xs text-green-600">Your answer has been sent to the student</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Input Area - Only show for pending questions */}
      {question.status === 'pending' && !answerSubmitted && (
        <div className="bg-white border-t border-gray-200 p-3 sm:p-4">
          <form onSubmit={handleSubmitAnswer} className="flex items-end gap-2">
            <div className="flex-1 bg-gray-100 rounded-2xl px-4 py-2 flex items-center gap-2">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitAnswer(e);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  toast.error('Pasting is not allowed. Please type your answer.');
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  toast.error('Copying is not allowed.');
                }}
                onCut={(e) => {
                  e.preventDefault();
                  toast.error('Cutting is not allowed.');
                }}
                placeholder="Type your answer..."
                className="flex-1 bg-transparent border-none outline-none resize-none text-sm sm:text-base max-h-32 py-1"
                rows="1"
                style={{ minHeight: '24px' }}
                disabled={submitting}
              />

              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Paperclip className="w-5 h-5" />
              </button>
            </div>

            <button
              type="submit"
              disabled={!answer.trim() || submitting}
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
                answer.trim() && !submitting
                  ? 'bg-secondary-500 hover:bg-secondary-600 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MentorQuestionDetail;
